import { useState } from 'react'
import { X, Check, Loader2 } from 'lucide-react'

/**
 * NuovoObiettivoForm — Modal per aggiungere o modificare un obiettivo PEP
 *
 * Modalità:
 *   - Nuovo: pepId + onConfirm(pepId, data) + onClose
 *   - Edit:  pepId + obiettivo + onConfirm(pepId, data) + onClose
 *
 * In edit mode i campi sono pre-compilati e appare un dropdown stato.
 *
 * Props:
 *   pepId       - ID del PEP
 *   obiettivo   - (opzionale) oggetto obiettivo esistente → attiva edit mode
 *   onConfirm   - callback(pepId, obiettivoData)
 *   onClose     - callback chiusura
 */

const AREA_OPTIONS = [
  { value: 'autonomia', label: 'Autonomia' },
  { value: 'socializzazione', label: 'Socializzazione' },
  { value: 'gestione_emotiva', label: 'Gestione emotiva' },
  { value: 'apprendimento', label: 'Apprendimento' },
  { value: 'relazioni_familiari', label: 'Relazioni familiari' },
  { value: 'comportamento', label: 'Comportamento' },
  { value: 'cura_di_se', label: 'Cura di sé' },
  { value: 'rispetto_regole', label: 'Rispetto regole' },
  { value: 'motivazione', label: 'Motivazione' },
  { value: 'altro', label: 'Altro' }
]

const FREQUENZA_OPTIONS = [
  { value: 'mensile', label: 'Mensile' },
  { value: 'bimestrale', label: 'Bimestrale' },
  { value: 'trimestrale', label: 'Trimestrale' },
  { value: 'semestrale', label: 'Semestrale' }
]

const REGOLE_COLLEGABILI = [
  { value: 'R01', label: 'R01 — Trend presenze', unit: '% presenze' },
  { value: 'R08', label: 'R08 — Trend comportamenti problematici', unit: 'episodi/settimana' },
  { value: 'R11', label: 'R11 — Settimane senza episodi', unit: 'settimane consecutive' },
  { value: 'R12', label: 'R12 — Trend svolgimento compiti', unit: '% compiti autonomi' },
  { value: 'R14', label: 'R14 — Partecipazione attività di gruppo', unit: '% giorni con attività' },
  { value: 'R15', label: 'R15 — Varietà attività', unit: 'attività diverse' },
  { value: 'R17', label: 'R17 — Frequenza segnalazioni urgenti', unit: 'segnalazioni/periodo' }
]

export default function NuovoObiettivoForm({ pepId, obiettivo, onConfirm, onClose }) {
  const isEdit = !!obiettivo
  const [submitting, setSubmitting] = useState(false)
  const [errors, setErrors] = useState({})

  const [area, setArea] = useState(obiettivo?.area || '')
  const [areaCustom, setAreaCustom] = useState(obiettivo?.areaCustom || '')
  const [titolo, setTitolo] = useState(obiettivo?.titolo || '')
  const [descrizione, setDescrizione] = useState(obiettivo?.descrizione || '')
  const [strategia, setStrategia] = useState(obiettivo?.strategia || '')
  const [priorita, setPriorita] = useState(obiettivo?.priorita || 'media')
  const [indicatoreTipo, setIndicatoreTipo] = useState(obiettivo?.indicatore?.tipo || 'qualitativo')
  const [regolaId, setRegolaId] = useState(obiettivo?.indicatore?.regolaInsightId || '')
  const [baseline, setBaseline] = useState(obiettivo?.indicatore?.valoreBaseline ?? '')
  const [target, setTarget] = useState(obiettivo?.indicatore?.valoreTarget ?? '')
  const [etichettaValore, setEtichettaValore] = useState(obiettivo?.indicatore?.etichettaValore || '')
  const [criterio, setCriterio] = useState(obiettivo?.indicatore?.criterio || '')
  const [frequenza, setFrequenza] = useState(obiettivo?.frequenzaVerifica || 'trimestrale')
  const [dataInizio, setDataInizio] = useState(
    obiettivo?.dataInizio ? toDateInputValue(new Date(obiettivo.dataInizio)) : toDateInputValue(new Date())
  )
  const [stato, setStato] = useState(obiettivo?.stato || 'attivo')

  const validate = () => {
    const errs = {}
    if (!area) errs.area = "L'area è obbligatoria"
    if (area === 'altro' && !areaCustom.trim()) errs.areaCustom = "Specifica l'area"
    if (!titolo.trim()) errs.titolo = 'Il titolo è obbligatorio'
    if (indicatoreTipo === 'automatico') {
      if (!regolaId) errs.regolaId = 'Seleziona una regola'
      if (baseline === '') errs.baseline = 'Il valore baseline è obbligatorio'
      if (target === '') errs.target = 'Il valore target è obbligatorio'
    }
    if (indicatoreTipo === 'qualitativo' && !criterio.trim()) {
      errs.criterio = 'Il criterio qualitativo è obbligatorio'
    }
    setErrors(errs)
    return Object.keys(errs).length === 0
  }

  const handleRegolaChange = (value) => {
    setRegolaId(value)
    const regola = REGOLE_COLLEGABILI.find(r => r.value === value)
    if (regola) setEtichettaValore(regola.unit)
  }

  const handleSubmit = async () => {
    if (!validate()) return
    setSubmitting(true)
    try {
      const obData = {
        area,
        ...(area === 'altro' && { areaCustom: areaCustom.trim() }),
        titolo: titolo.trim(),
        ...(descrizione.trim() && { descrizione: descrizione.trim() }),
        ...(strategia.trim() && { strategia: strategia.trim() }),
        priorita,
        frequenzaVerifica: frequenza,
        dataInizio: new Date(dataInizio).toISOString(),
        indicatore: { tipo: indicatoreTipo }
      }

      if (indicatoreTipo === 'automatico') {
        obData.indicatore.regolaInsightId = regolaId
        obData.indicatore.valoreBaseline = Number(baseline)
        obData.indicatore.valoreTarget = Number(target)
        obData.indicatore.etichettaValore = etichettaValore.trim() ||
          REGOLE_COLLEGABILI.find(r => r.value === regolaId)?.unit || ''
      } else {
        obData.indicatore.criterio = criterio.trim()
      }

      if (isEdit) {
        obData.stato = stato
      }

      await onConfirm(pepId, obData)
    } catch {
      // errore gestito dal parent
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center overflow-y-auto">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-xl my-4 mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {isEdit ? 'Modifica obiettivo' : 'Nuovo obiettivo'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <div className="px-6 py-5 space-y-5 max-h-[calc(100vh-200px)] overflow-y-auto">
          {/* Area + Priorità */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Area di intervento *
              </label>
              <select
                value={area}
                onChange={(e) => setArea(e.target.value)}
                className={`form-input w-full ${errors.area ? 'border-red-500' : ''}`}
              >
                <option value="">Seleziona...</option>
                {AREA_OPTIONS.map(a => (
                  <option key={a.value} value={a.value}>{a.label}</option>
                ))}
              </select>
              {errors.area && <p className="text-xs text-red-500 mt-1">{errors.area}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Priorità
              </label>
              <div className="flex gap-2 mt-1.5">
                {['alta', 'media', 'bassa'].map(p => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setPriorita(p)}
                    className={`flex-1 px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                      priorita === p
                        ? p === 'alta' ? 'border-red-400 bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400'
                        : p === 'media' ? 'border-orange-400 bg-orange-50 text-orange-700 dark:bg-orange-900/20 dark:text-orange-400'
                        : 'border-green-400 bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400'
                        : 'border-gray-200 dark:border-gray-600 text-gray-500 hover:border-gray-300'
                    }`}
                  >
                    {p.charAt(0).toUpperCase() + p.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Area custom */}
          {area === 'altro' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Specifica area *
              </label>
              <input
                type="text"
                value={areaCustom}
                onChange={(e) => setAreaCustom(e.target.value)}
                placeholder="Descrivi l'area..."
                className={`form-input w-full ${errors.areaCustom ? 'border-red-500' : ''}`}
                maxLength={100}
              />
              {errors.areaCustom && <p className="text-xs text-red-500 mt-1">{errors.areaCustom}</p>}
            </div>
          )}

          {/* Titolo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Titolo obiettivo *
            </label>
            <input
              type="text"
              value={titolo}
              onChange={(e) => setTitolo(e.target.value)}
              placeholder="Es: Ridurre episodi aggressivi, Migliorare autonomia nei compiti..."
              className={`form-input w-full ${errors.titolo ? 'border-red-500' : ''}`}
              maxLength={200}
            />
            {errors.titolo && <p className="text-xs text-red-500 mt-1">{errors.titolo}</p>}
          </div>

          {/* Descrizione */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Descrizione
              <span className="text-gray-400 font-normal ml-1">(opzionale)</span>
            </label>
            <textarea
              value={descrizione}
              onChange={(e) => setDescrizione(e.target.value)}
              placeholder="Descrizione dettagliata dell'obiettivo..."
              rows={2}
              className="form-input w-full resize-none"
              maxLength={2000}
            />
          </div>

          {/* Toggle indicatore */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Tipo indicatore di verifica
            </label>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setIndicatoreTipo('qualitativo')}
                className={`flex-1 p-3 rounded-lg border-2 text-left transition-colors ${
                  indicatoreTipo === 'qualitativo'
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
                }`}
              >
                <p className={`font-medium text-sm ${indicatoreTipo === 'qualitativo' ? 'text-primary-700 dark:text-primary-400' : 'text-gray-700 dark:text-gray-300'}`}>
                  Qualitativo
                </p>
                <p className="text-xs text-gray-500 mt-0.5">Criterio descrittivo</p>
              </button>
              <button
                type="button"
                onClick={() => setIndicatoreTipo('automatico')}
                className={`flex-1 p-3 rounded-lg border-2 text-left transition-colors ${
                  indicatoreTipo === 'automatico'
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
                }`}
              >
                <p className={`font-medium text-sm ${indicatoreTipo === 'automatico' ? 'text-primary-700 dark:text-primary-400' : 'text-gray-700 dark:text-gray-300'}`}>
                  Automatico
                </p>
                <p className="text-xs text-gray-500 mt-0.5">Collegato ai dati del diario</p>
              </button>
            </div>
          </div>

          {/* Qualitativo */}
          {indicatoreTipo === 'qualitativo' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Criterio di verifica *
              </label>
              <textarea
                value={criterio}
                onChange={(e) => setCriterio(e.target.value)}
                placeholder="Es: Il minore riesce a gestire la frustrazione senza reagire fisicamente"
                rows={3}
                className={`form-input w-full resize-none ${errors.criterio ? 'border-red-500' : ''}`}
                maxLength={500}
              />
              {errors.criterio && <p className="text-xs text-red-500 mt-1">{errors.criterio}</p>}
            </div>
          )}

          {/* Automatico */}
          {indicatoreTipo === 'automatico' && (
            <div className="space-y-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Regola insight *
                </label>
                <select
                  value={regolaId}
                  onChange={(e) => handleRegolaChange(e.target.value)}
                  className={`form-input w-full ${errors.regolaId ? 'border-red-500' : ''}`}
                >
                  <option value="">Seleziona regola...</option>
                  {REGOLE_COLLEGABILI.map(r => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
                {errors.regolaId && <p className="text-xs text-red-500 mt-1">{errors.regolaId}</p>}
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Baseline *</label>
                  <input
                    type="number"
                    value={baseline}
                    onChange={(e) => setBaseline(e.target.value)}
                    placeholder="0"
                    className={`form-input w-full ${errors.baseline ? 'border-red-500' : ''}`}
                  />
                  {errors.baseline && <p className="text-xs text-red-500 mt-1">{errors.baseline}</p>}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Target *</label>
                  <input
                    type="number"
                    value={target}
                    onChange={(e) => setTarget(e.target.value)}
                    placeholder="0"
                    className={`form-input w-full ${errors.target ? 'border-red-500' : ''}`}
                  />
                  {errors.target && <p className="text-xs text-red-500 mt-1">{errors.target}</p>}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Unità</label>
                  <input
                    type="text"
                    value={etichettaValore}
                    onChange={(e) => setEtichettaValore(e.target.value)}
                    placeholder="es: episodi/mese"
                    className="form-input w-full"
                    maxLength={50}
                  />
                </div>
              </div>

              {baseline !== '' && target !== '' && (
                <p className="text-xs text-gray-500">
                  Obiettivo: da <strong>{baseline}</strong> a <strong>{target}</strong> {etichettaValore}
                </p>
              )}
            </div>
          )}

          {/* Frequenza + Data inizio */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Frequenza verifica
              </label>
              <select
                value={frequenza}
                onChange={(e) => setFrequenza(e.target.value)}
                className="form-input w-full"
              >
                {FREQUENZA_OPTIONS.map(f => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Data inizio
              </label>
              <input
                type="date"
                value={dataInizio}
                onChange={(e) => setDataInizio(e.target.value)}
                className="form-input w-full"
              />
            </div>
          </div>

          {/* Strategia */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Strategia / intervento previsto
              <span className="text-gray-400 font-normal ml-1">(opzionale)</span>
            </label>
            <textarea
              value={strategia}
              onChange={(e) => setStrategia(e.target.value)}
              placeholder="Come si intende raggiungere l'obiettivo..."
              rows={3}
              className="form-input w-full resize-none"
              maxLength={2000}
            />
          </div>

          {/* Stato — solo in edit mode */}
          {isEdit && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Stato obiettivo
              </label>
              <select
                value={stato}
                onChange={(e) => setStato(e.target.value)}
                className="form-input w-full"
              >
                <option value="attivo">Attivo</option>
                <option value="sospeso">Sospeso</option>
              </select>
              {stato === 'sospeso' && (
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                  Un obiettivo sospeso non riceverà verifiche automatiche e non comparirà nelle scadenze.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            disabled={submitting}
            className="text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 px-3 py-2"
          >
            Annulla
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="flex items-center gap-2 text-sm font-medium bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Check className="h-4 w-4" />
            )}
            {isEdit ? 'Salva modifiche' : 'Aggiungi obiettivo'}
          </button>
        </div>
      </div>
    </div>
  )
}

function toDateInputValue(date) {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}
